// src/pages/ResetPasswordConfirmPage.js
import axios from '../utils/axiosConfig';
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../Style/ResetPasswordConfirm.css';

const ResetPasswordConfirmPage = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();

  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError]                     = useState('');
  const [success, setSuccess]                 = useState('');
  const [loading, setLoading]                 = useState(false);

  const validatePassword = (pw) => {
    if (pw.length > 128)        return 'Password cannot exceed 128 characters.';
    if (pw.length < 8)          return 'Password must be at least 8 characters.';
    if (!/[a-z]/.test(pw))      return 'Include at least one lowercase letter.';
    if (!/[A-Z]/.test(pw))      return 'Include at least one uppercase letter.';
    if (!/[0-9]/.test(pw))      return 'Include at least one number.';
    if (!/[^A-Za-z0-9]/.test(pw)) return 'Include at least one special character.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    const pwErr = validatePassword(newPassword);
    if (pwErr) {
      setError(pwErr);
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reset-password-confirm/${uid}/${token}/`,
        { uid, token, password: newPassword },
        { headers: { 'X-Skip-Auth-Redirect': '1' } } // ← never let axios interceptor redirect this page
      );

      // Only now, after a successful reset, log out locally
      localStorage.removeItem('token');
      localStorage.removeItem('user_type');

      setSuccess('Your password has been reset. Redirecting to login…');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const serverError = err?.response?.data?.error || '';
      if (err.response?.status === 429) {
        setError('Too many attempts. Please try again in 3 minutes.');
      } else if (serverError === 'You cannot reuse your old passwords.') {
        setError("You’ve used this password before. Please choose a new one.");
      } else if (serverError === 'Invalid or expired token.') {
        setError('This reset link is no longer valid. Please request a new one.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-confirm-container">
  
      <h2>Set New Password</h2>

      {error   && <p className="error-message" role="alert">{error}</p>}
      {success && <p className="success-message">{success}</p>}

      <form onSubmit={handleSubmit} className="reset-password-confirm-form" autoComplete="off">
        <label htmlFor="new-password">New Password</label>
        <input
          id="new-password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          disabled={loading}
          autoComplete="off"
        />

        {/* simple checklist */}
        <ul className="pw-req-list">
          <li className={newPassword.length >= 8 ? 'ok' : ''}>At least 8 characters</li>
          <li className={/[a-z]/.test(newPassword) ? 'ok' : ''}>Lowercase letter</li>
          <li className={/[A-Z]/.test(newPassword) ? 'ok' : ''}>Uppercase letter</li>
          <li className={/[0-9]/.test(newPassword) ? 'ok' : ''}>Number</li>
          <li className={/[^A-Za-z0-9]/.test(newPassword) ? 'ok' : ''}>Special character</li>
          <li className={newPassword.length <= 128 && newPassword ? 'ok' : ''}>Not too long (≤128)</li>
          <li className={newPassword && newPassword === confirmPassword ? 'ok' : ''}>Passwords match</li>
        </ul>

        <label htmlFor="confirm-password">Confirm New Password</label>
        <input
          id="confirm-password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={loading}
          autoComplete="off"
        />

        <button type="submit" disabled={loading}>
          {loading ? 'Submitting…' : 'Reset Password'}
        </button>
      </form>
    </div>
  );
};

export default ResetPasswordConfirmPage;
